import {ChangeEvent, FC, useRef, useState} from 'react';
import {Area} from 'react-easy-crop';
import {Box, Flex, Image, Text} from '@chakra-ui/react';
import {api, useT} from '@kcast-fe/core';
import {toastUtils, cropUtils} from 'core';
import {Loader} from 'ui-kit';

import {AvatarCropDialog} from './components';

const SIZE = '200px';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 Mb
const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

export const AvatarUpload: FC = () => {
  const {data: avatar, isLoading: isAvatarLoading} = api.avatar.useAvatarQuery();
  const {data: avatarImageUrl, isLoading: isImageLoading} = api.avatar.useAvatarImageQuery(avatar);
  const uploadMutation = api.avatar.useUploadAvatarMutation();
  const resetMutation = api.avatar.useResetAvatarMutation();

  const inputRef = useRef<HTMLInputElement>(null);
  const [cropSource, setCropSource] = useState<{file: File; url: string} | null>(null);

  const isPending = uploadMutation.isPending || resetMutation.isPending;
  const isLoading = isAvatarLoading || isImageLoading;

  const {t} = useT();

  const handleClick = () => {
    if (!isPending) {
      inputRef.current?.click();
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) {
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type) || file.size > MAX_FILE_SIZE) {
      toastUtils.error({message: t('avatar.uploadError')});
      return;
    }

    setCropSource({file, url: URL.createObjectURL(file)});
  };

  const closeCrop = () => {
    setCropSource((current) => {
      if (current) {
        URL.revokeObjectURL(current.url);
      }
      return null;
    });
  };

  const handleCropConfirm = async (area: Area) => {
    if (!cropSource) {
      return;
    }

    try {
      const croppedFile = await cropUtils(
        cropSource.url,
        area,
        cropSource.file.type,
        cropSource.file.name
      );

      uploadMutation.mutate(croppedFile, {
        onSuccess: closeCrop,
        onError: () => toastUtils.error({message: t('errors.uploadFailed')})
      });
    } catch {
      toastUtils.error({message: t('errors.uploadFailed')});
    }
  };

  const handleReset = () => {
    if (!isPending) {
      resetMutation.mutate(undefined, {
        onError: () => toastUtils.error({message: t('errors.wentWrong')})
      });
    }
  };

  return (
    <Flex direction={'column'} align={'center'} gap={'12px'}>
      <Box
        w={SIZE}
        h={SIZE}
        role={'button'}
        position={'relative'}
        borderRadius={'full'}
        boxShadow={'{shadows.shadow_menu}'}
        cursor={isPending ? 'default' : 'pointer'}
        overflow={'hidden'}
        border={'1px dashed'}
        borderColor={'{colors.borderDefault}'}
        css={{
          '&:hover .avatar-overlay': {opacity: isPending || isLoading ? 0 : 1}
        }}
        onClick={handleClick}
      >
        {isLoading ? (
          <Flex w={'100%'} h={'100%'} align={'center'} justify={'center'}>
            <Loader size={'md'} />
          </Flex>
        ) : (
          <Image
            w={'100%'}
            h={'100%'}
            alt={'avatar'}
            src={avatarImageUrl}
            objectFit={'cover'}
            borderRadius={'full'}
          />
        )}

        <Flex
          position={'absolute'}
          inset={'0'}
          opacity={0}
          borderRadius={'full'}
          bg={'whiteAlpha.500'}
          align={'center'}
          justify={'center'}
          transition={'all 0.15s ease'}
          pointerEvents={'none'}
          className={'avatar-overlay'}
        >
          {isPending ? (
            <Loader size={'sm'} />
          ) : (
            <Text px={'8px'} fontSize={'14px'} color={'{colors.white}'} textAlign={'center'}>
              {t('avatar.changeImage')}
            </Text>
          )}
        </Flex>
      </Box>

      <input
        ref={inputRef}
        type={'file'}
        accept={'.jpg,.jpeg,.png'}
        style={{display: 'none'}}
        onChange={handleFileChange}
      />

      <Box h={'20px'}>
        {isPending && <Text fontSize={'md'}>{t('labels.changing')}...</Text>}

        {avatar && !avatar.is_default && !isPending && (
          <Text
            as={'button'}
            fontSize={'md'}
            color={'{colors.textLink}'}
            cursor={'pointer'}
            onClick={handleReset}
          >
            {t('avatar.resetToDefault')}
          </Text>
        )}
      </Box>

      {cropSource && (
        <AvatarCropDialog
          imageSrc={cropSource.url}
          isPending={uploadMutation.isPending}
          onCancel={closeCrop}
          onConfirm={handleCropConfirm}
        />
      )}
    </Flex>
  );
};
